// Shared filter logic for the Organic Assets dimensions, used by BOTH the
// rollout master grid (src/modules/rollouts/FilterBar + MasterGrid) and the
// Meta role grid (gridFilterUrl + applyGridFilters + GridFilterBar). Defining
// the value enums, option lists, and predicates once keeps the two grids from
// drifting.
//
// Both dimensions are multi-select: selected options OR together WITHIN the
// dimension and AND with every other active filter (search, AM, status, …).
// An empty selection means "no constraint".

import type { AccountCampaign } from '../types/accountCampaign.js';

// ---------- Asset folder presence ----------

// Reads the server-derived folder-presence cache (hasLocalFiles /
// hasOrganicFiles). Optional/undefined on legacy rows → treated as false.
export type AssetPresenceFilter = 'hasLocal' | 'noLocal' | 'hasOrganic' | 'noOrganic';

export const ASSET_PRESENCE_OPTIONS: ReadonlyArray<{
  value: AssetPresenceFilter;
  label: string;
}> = [
  { value: 'hasLocal', label: 'Has Local Assets' },
  { value: 'noLocal', label: 'No Local Assets' },
  { value: 'hasOrganic', label: 'Has Organic Assets' },
  { value: 'noOrganic', label: 'No Organic Assets' },
];

const ASSET_PRESENCE_SET: ReadonlySet<string> = new Set(
  ASSET_PRESENCE_OPTIONS.map((o) => o.value),
);

// Filter a raw string list (e.g. from a URL param) down to valid values.
export function sanitizeAssetPresence(values: readonly string[]): AssetPresenceFilter[] {
  return values.filter((v): v is AssetPresenceFilter => ASSET_PRESENCE_SET.has(v));
}

// True when the row satisfies ANY selected presence option (OR within the
// dimension). Empty selection = no constraint.
export function matchesAssetPresence(
  row: Pick<AccountCampaign, 'hasLocalFiles' | 'hasOrganicFiles'>,
  selected: readonly AssetPresenceFilter[],
): boolean {
  if (selected.length === 0) return true;
  const hasLocal = row.hasLocalFiles ?? false;
  const hasOrganic = row.hasOrganicFiles ?? false;
  for (const opt of selected) {
    if (opt === 'hasLocal' && hasLocal) return true;
    if (opt === 'noLocal' && !hasLocal) return true;
    if (opt === 'hasOrganic' && hasOrganic) return true;
    if (opt === 'noOrganic' && !hasOrganic) return true;
  }
  return false;
}

// ---------- Build status ("not built") ----------

// "Not built" means the build checkbox is exposed-but-unticked — i.e. an
// actionable build to-do.
//   - 'mainNotBuilt'    → the Meta "Main Built" checkbox (metaBuilt). NOT
//                         presence-gated — the national / main assets have no
//                         folder-presence cache and the checkbox is always
//                         shown — so this is simply metaBuilt === false.
//   - 'localNotBuilt'   → metaLocalBuilt, presence-gated on hasLocalFiles.
//   - 'organicNotBuilt' → metaOrganicBuilt, presence-gated on hasOrganicFiles.
//                         Local / Organic checkboxes only EXIST once the folder
//                         has files (shouldRenderMeta{Local,Organic}BuiltCell),
//                         so a no-files row is not a build to-do → excluded.
//   - 'googleNotBuilt'  → the Google "Main Built" checkbox (googleBuilt). Like
//                         Meta main, NOT presence-gated (Google has no
//                         Local / Organic concept).
//
// The first three are Meta dimensions; 'googleNotBuilt' is the Google one. The
// master grid offers all four; the Meta / Google role grids offer only their
// own platform's subset (META_ / GOOGLE_BUILD_STATUS_OPTIONS).
export type BuildStatusFilter =
  | 'mainNotBuilt'
  | 'localNotBuilt'
  | 'organicNotBuilt'
  | 'googleNotBuilt';

// Full list — the master grid's "Build status" dropdown. "Main" carries a
// platform prefix in the label because BOTH Meta and Google have a "Main
// Built" column; Local / Organic are unambiguously Meta sub-builds so they
// stay unprefixed.
export const BUILD_STATUS_OPTIONS: ReadonlyArray<{
  value: BuildStatusFilter;
  label: string;
}> = [
  { value: 'mainNotBuilt', label: 'Meta Main not built' },
  { value: 'localNotBuilt', label: 'Local not built' },
  { value: 'organicNotBuilt', label: 'Organic not built' },
  { value: 'googleNotBuilt', label: 'Google Main not built' },
];

// Per-role-grid subsets. The Meta grid shows the three Meta dimensions; the
// Google grid shows only the Google one. (The master grid uses the full list.)
export const META_BUILD_STATUS_OPTIONS = BUILD_STATUS_OPTIONS.filter(
  (o) => o.value !== 'googleNotBuilt',
);
export const GOOGLE_BUILD_STATUS_OPTIONS = BUILD_STATUS_OPTIONS.filter(
  (o) => o.value === 'googleNotBuilt',
);

const BUILD_STATUS_SET: ReadonlySet<string> = new Set(
  BUILD_STATUS_OPTIONS.map((o) => o.value),
);

export function sanitizeBuildStatus(values: readonly string[]): BuildStatusFilter[] {
  return values.filter((v): v is BuildStatusFilter => BUILD_STATUS_SET.has(v));
}

// Restrict a selection to the options a given role grid offers, so a stale /
// hand-crafted URL can't filter (say) the Meta grid by the Google build
// checkbox. The master grid applies the full selection and does NOT call this.
export function buildStatusForPlatform(
  selected: readonly BuildStatusFilter[],
  platform: 'meta' | 'google',
): BuildStatusFilter[] {
  const allowed = new Set(
    (platform === 'meta' ? META_BUILD_STATUS_OPTIONS : GOOGLE_BUILD_STATUS_OPTIONS).map(
      (o) => o.value,
    ),
  );
  return selected.filter((v) => allowed.has(v));
}

// True when the row satisfies ANY selected "not built" option (OR within the
// dimension). Empty selection = no constraint. Local / Organic require file
// presence (their checkbox only exists when the folder has files); Main (Meta)
// and Google are not presence-gated (always exposed). A missing/undefined
// checkbox counts as not ticked; missing/undefined presence (legacy rows)
// counts as no files → Local / Organic excluded.
export function matchesBuildStatus(
  row: Pick<
    AccountCampaign,
    | 'metaBuilt'
    | 'metaLocalBuilt'
    | 'metaOrganicBuilt'
    | 'googleBuilt'
    | 'hasLocalFiles'
    | 'hasOrganicFiles'
  >,
  selected: readonly BuildStatusFilter[],
): boolean {
  if (selected.length === 0) return true;
  const hasLocal = row.hasLocalFiles ?? false;
  const hasOrganic = row.hasOrganicFiles ?? false;
  for (const opt of selected) {
    if (opt === 'mainNotBuilt' && !row.metaBuilt) return true;
    if (opt === 'localNotBuilt' && hasLocal && !row.metaLocalBuilt) return true;
    if (opt === 'organicNotBuilt' && hasOrganic && !row.metaOrganicBuilt) return true;
    if (opt === 'googleNotBuilt' && !row.googleBuilt) return true;
  }
  return false;
}
